import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { Product, ProductVariant, fetchPublicProductDetail } from '@/services/products';
import { useCart } from '@/contexts/CartContext';
import ProductGallery from './components/ProductGallery';
import ProductFeatures from './components/ProductFeatures';
import ProductSpecs from './components/ProductSpecs';
import RelatedProducts from './components/RelatedProducts';

function ProductCartSection({
  product,
  activeVariants,
  selectedVariant,
  selectedOptionValues,
  onSelectOption,
}: {
  product: Product;
  activeVariants: ProductVariant[];
  selectedVariant: ProductVariant | null;
  selectedOptionValues: Record<string, string>;
  onSelectOption: (attributeName: string, value: string) => void;
}) {
  const { addToCart, openCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [feedback, setFeedback] = useState('');

  const maxStock = product.hasVariants ? (selectedVariant ? selectedVariant.stock : 0) : product.stock;
  const isOutOfStock = maxStock !== null && maxStock <= 0;
  const unitPrice = selectedVariant ? (selectedVariant.salePrice ?? selectedVariant.price) : (product.salePrice ?? product.price);
  const originalPrice = selectedVariant ? (selectedVariant.salePrice !== null ? selectedVariant.price : null) : (product.salePrice !== null ? product.price : null);

  useEffect(() => {
    setQuantity(1);
  }, [selectedVariant?.id]);

  function handleSelectOption(attributeName: string, value: string) {
    onSelectOption(attributeName, value);
  }

  function handleAddToCart() {
    const result = addToCart(product, selectedVariant, quantity);
    if (result.ok) {
      setFeedback('');
      openCart();
    } else {
      setFeedback(result.message || '無法加入購物車。');
    }
  }

  return (
    <div className="mt-6 rounded-xl border border-[#E8E0D6] bg-[#FAF8F5] p-5 md:p-6">
      {product.hasVariants && (
        <div className="grid gap-4">
          {product.variantAttributes.map((attribute) => (
            <div key={attribute.name}>
              <div className="mb-2 flex items-baseline gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#9A8A7A]">
                {attribute.name}
                {selectedOptionValues[attribute.name] && (
                  <span className="normal-case tracking-normal text-[#1B3D2A]">· {selectedOptionValues[attribute.name]}</span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {attribute.values.map((value) => {
                  const isAvailable = activeVariants.some((variant) => variant.optionValues[attribute.name] === value);
                  const isSelected = selectedOptionValues[attribute.name] === value;
                  return (
                    <button
                      type="button"
                      key={value}
                      disabled={!isAvailable}
                      onClick={() => handleSelectOption(attribute.name, value)}
                      title={isAvailable ? undefined : '此選項目前缺貨或未提供'}
                      className={`relative rounded-full border px-4 py-2 text-sm font-semibold transition ${
                        isSelected
                          ? 'border-[#1B3D2A] bg-[#1B3D2A] text-white'
                          : isAvailable
                            ? 'border-[#E8E0D6] bg-white text-[#5A6B5A] hover:border-[#1B3D2A] hover:text-[#1B3D2A]'
                            : 'border-[#E8E0D6] bg-[#FAF8F5] text-[#C9BFAF] cursor-not-allowed'
                      }`}
                    >
                      {value}
                      {!isAvailable && (
                        <span className="pointer-events-none absolute left-1/2 top-1/2 h-px w-[70%] -translate-x-1/2 -translate-y-1/2 -rotate-[18deg] bg-[#C9BFAF]" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {!selectedVariant && (
            <p className="text-sm font-medium text-[#c3423f]">此規格組合目前無法購買，請選擇其他組合。</p>
          )}
        </div>
      )}

      <div className={`flex flex-wrap items-center justify-between gap-4 ${product.hasVariants ? 'mt-5 border-t border-[#E8E0D6] pt-5' : ''}`}>
        <div className="flex items-baseline gap-2">
          <span className="text-sm text-[#9A8A7A]">NT$</span>
          <span className="text-3xl font-bold text-[#1B3D2A]">{unitPrice.toLocaleString()}</span>
          {originalPrice !== null && <span className="text-base font-semibold text-[#9A8A7A] line-through">NT$ {originalPrice.toLocaleString()}</span>}
        </div>
        <div className="flex items-center gap-1 rounded-full border border-[#E8E0D6] bg-white">
          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center text-[#5A6B5A] hover:text-[#1B3D2A] disabled:opacity-40"
            onClick={() => setQuantity((current) => Math.max(1, current - 1))}
            disabled={quantity <= 1}
            aria-label="減少數量"
          >
            <i className="ri-subtract-line" />
          </button>
          <span className="w-8 text-center text-sm font-bold text-[#1B3D2A]">{quantity}</span>
          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center text-[#5A6B5A] hover:text-[#1B3D2A] disabled:opacity-40"
            onClick={() => setQuantity((current) => (maxStock !== null ? Math.min(current + 1, maxStock) : current + 1))}
            disabled={maxStock !== null && quantity >= maxStock}
            aria-label="增加數量"
          >
            <i className="ri-add-line" />
          </button>
        </div>
      </div>

      {maxStock !== null && !isOutOfStock && maxStock <= 5 && (
        <p className="mt-2 text-xs font-semibold text-[#D4A574]">庫存僅剩 {maxStock} 件</p>
      )}

      <button
        type="button"
        onClick={handleAddToCart}
        disabled={isOutOfStock || (product.hasVariants && !selectedVariant)}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#1B3D2A] py-3.5 text-base font-bold text-white transition-colors hover:bg-[#143026] disabled:cursor-not-allowed disabled:opacity-50"
      >
        <i className="ri-shopping-cart-2-line" />
        {isOutOfStock ? '已無庫存' : '加入購物車'}
      </button>
      {feedback && <p className="mt-2 text-sm font-medium text-[#c3423f]">{feedback}</p>}
    </div>
  );
}

function SectionReveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div
      ref={ref}
      className={`scroll-reveal ${isVisible ? 'scroll-reveal-visible' : ''} ${delay > 0 ? `scroll-reveal-delay-${Math.min(delay, 6)}` : ''} ${className}`}
    >
      {children}
    </div>
  );
}

function ProductDescriptionSection({ description }: { description: string }) {
  if (!description.trim()) return null;

  return (
    <SectionReveal>
      <div className="overflow-hidden rounded-2xl border border-[#E8E0D6] bg-[#FAF8F5] p-6 md:p-10">
        <div className="mb-6 flex items-center gap-2 md:mb-8">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1B3D2A]/10">
            <div className="flex h-4 w-4 items-center justify-center">
              <i className="ri-file-list-3-line text-[#1B3D2A]" />
            </div>
          </div>
          <span className="text-xs font-semibold uppercase tracking-wider text-[#D4A574]">Details</span>
        </div>
        <h2 className="mb-5 text-xl font-bold text-[#1B3D2A] md:mb-6 md:text-2xl">
          產品詳細介紹
        </h2>
        <div
          className="max-w-full min-w-0 overflow-x-auto break-words text-sm leading-7 text-[#5A6B5A] [overflow-wrap:anywhere] md:text-base [&_*]:max-w-full [&_a]:font-bold [&_a]:text-[#1B3D2A] [&_blockquote]:border-l-4 [&_blockquote]:border-[#d7c7b6] [&_blockquote]:pl-4 [&_font[size='2']]:text-sm [&_font[size='4']]:text-lg [&_font[size='5']]:text-xl [&_h2]:mb-3 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-[#1B3D2A] [&_h3]:mb-2 [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-[#1B3D2A] [&_iframe]:my-5 [&_iframe]:aspect-video [&_iframe]:h-auto [&_iframe]:w-full [&_iframe]:rounded-xl [&_iframe]:border-0 [&_img]:my-5 [&_img]:h-auto [&_img]:max-h-[560px] [&_img]:w-full [&_img]:rounded-xl [&_img]:object-contain [&_li]:ml-5 [&_ol]:list-decimal [&_p]:mb-4 [&_pre]:max-w-full [&_pre]:overflow-x-auto [&_strong]:font-bold [&_strong]:text-[#1B3D2A] [&_table]:w-full [&_table]:min-w-max [&_ul]:list-disc"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      </div>
    </SectionReveal>
  );
}

function ProductDetailSkeleton() {
  return (
    <div className="min-h-screen overflow-x-hidden bg-white">
      <Navbar />
      <main className="pt-14 md:pt-20">
        <div className="relative overflow-hidden bg-[#1B3D2A] px-6 py-8 text-white md:px-10 lg:px-16">
          <div className="mx-auto max-w-7xl">
            <div className="h-4 w-56 animate-pulse rounded-full bg-white/20" />
            <div className="mt-4 h-8 w-72 animate-pulse rounded-full bg-white/25" />
          </div>
        </div>
        <div className="mx-auto grid max-w-7xl gap-8 px-6 py-8 md:px-10 md:py-14 lg:grid-cols-2 lg:px-16">
          <div>
            <div className="h-72 animate-pulse rounded-2xl bg-[#F3EFE8] md:h-[500px]" />
            <div className="mt-4 flex gap-3">
              {[0, 1, 2].map((item) => <div className="h-16 w-16 animate-pulse rounded-xl bg-[#EFE8DE] md:h-20 md:w-20" key={item} />)}
            </div>
          </div>
          <div>
            <div className="h-4 w-40 animate-pulse rounded-full bg-[#E8E0D6]" />
            <div className="mt-5 h-8 w-72 animate-pulse rounded-full bg-[#E8E0D6]" />
            <div className="mt-5 flex gap-2">
              {[0, 1, 2].map((item) => <div className="h-8 w-20 animate-pulse rounded-full bg-[#EFE8DE]" key={item} />)}
            </div>
            <div className="mt-7 h-10 w-44 animate-pulse rounded-full bg-[#E8E0D6]" />
            <div className="mt-5 space-y-3">
              <div className="h-4 w-full animate-pulse rounded-full bg-[#EFE8DE]" />
              <div className="h-4 w-5/6 animate-pulse rounded-full bg-[#EFE8DE]" />
              <div className="h-4 w-2/3 animate-pulse rounded-full bg-[#EFE8DE]" />
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3 rounded-xl border border-[#E8E0D6] bg-[#FAF8F5] p-5">
              {[0, 1, 2, 3].map((item) => <div className="h-12 animate-pulse rounded-lg bg-[#EFE8DE]" key={item} />)}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default function ProductDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const productKey = id || "";
  const [product, setProduct] = useState<Product | null>(null);
  const [relatedSource, setRelatedSource] = useState<Product[]>([]);
  const [loadingProduct, setLoadingProduct] = useState(true);
  const [selectedOptionValues, setSelectedOptionValues] = useState<Record<string, string>>({});

  const activeVariants = useMemo(() => (product ? product.variants.filter((variant) => variant.isActive) : []), [product]);
  const defaultVariant = useMemo(() => activeVariants.find((variant) => variant.isDefault) || activeVariants[0] || null, [activeVariants]);
  const selectedVariant: ProductVariant | null = useMemo(() => {
    if (!product || !product.hasVariants) return null;
    return (
      activeVariants.find((variant) =>
        product.variantAttributes.every((attribute) => variant.optionValues[attribute.name] === selectedOptionValues[attribute.name])
      ) || null
    );
  }, [product, activeVariants, selectedOptionValues]);

  function handleSelectVariantOption(attributeName: string, value: string) {
    setSelectedOptionValues((current) => ({ ...current, [attributeName]: value }));
  }

  useEffect(() => {
    setSelectedOptionValues(defaultVariant?.optionValues || {});
  }, [product, defaultVariant]);

  useEffect(() => {
    let alive = true;
    setLoadingProduct(true);
    setProduct(null);
    fetchPublicProductDetail(productKey)
      .then((data) => {
        if (!alive) return;
        setProduct(data.product);
        setRelatedSource(data.relatedProducts);
      })
      .finally(() => {
        if (alive) setLoadingProduct(false);
      });
    return () => {
      alive = false;
    };
  }, [productKey]);

  useEffect(() => {
    if (!loadingProduct && !product) {
      navigate('/products');
    }
  }, [loadingProduct, product, navigate]);

  if (loadingProduct) return <ProductDetailSkeleton />;
  if (!product) return null;

  const isNew = product.isNew === true;
  const isHot = product.isHot === true;
  const displayPrice = product.salePrice || product.price;
  const quickInfoItems = (product.quickInfoItems || []).filter((item) => item.Label?.trim() || item.Value?.trim());
  const productTrialBookingPath = `/booking?${new URLSearchParams({
    productId: String(product.id),
    productName: product.name,
    productCode: product.productCode || '',
    type: 'ProductTrial',
  }).toString()}`;

  return (
    <div className="min-h-screen overflow-x-hidden bg-white">
      <Navbar bookingPath={productTrialBookingPath} />

      <main className="pt-14 md:pt-20">
        {/* Hero 區 */}
        <div className="relative min-h-[220px] overflow-hidden pt-7 pb-20 text-white md:min-h-[260px] md:pt-10 md:pb-24">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1B5E40] via-[#256F4A] to-[#1B3D2A]" />
          <div className="absolute -top-20 right-0 w-72 h-72 md:w-96 md:h-96 rounded-full bg-[#D4A574]/15 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 md:w-64 md:h-64 rounded-full bg-[#2E8B57]/20 blur-2xl" />

          <div className="relative z-10 mx-auto w-full max-w-7xl min-w-0 px-6 md:px-10 lg:px-16">
            {/* 麵包屑 */}
            <nav className="mb-6 flex flex-wrap items-center gap-2 text-sm text-white/76 md:mb-7">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="hover:text-white transition-colors"
              >
                首頁
              </button>
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </div>
              <button
                type="button"
                onClick={() => navigate('/products')}
                className="hover:text-white transition-colors"
              >
                產品系列
              </button>
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </div>
              <span className="min-w-0 flex-1 truncate font-medium text-white md:max-w-[32rem]">{product.name}</span>
            </nav>

            <h1 className="max-w-4xl break-words text-3xl font-bold leading-tight tracking-normal text-white drop-shadow-sm md:text-5xl md:leading-tight">
              {product.name}
            </h1>
          </div>

          <svg className="pointer-events-none absolute bottom-[-1px] left-0 block h-16 w-full md:h-24" viewBox="0 0 1440 120" fill="none" preserveAspectRatio="none">
            <path d="M0 120L60 108C120 96 240 72 360 60C480 48 600 48 720 54C840 60 960 72 1080 78C1200 84 1320 84 1380 84L1440 84V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>

        {/* 主產品區 */}
        <div className="px-6 md:px-10 lg:px-16 py-8 md:py-14 max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 md:gap-10 lg:gap-16">
            {/* 左側 - 圖片 */}
            <div className="min-w-0 lg:w-1/2">
              <SectionReveal>
                <ProductGallery
                  key={selectedVariant?.id ?? 'base'}
                  productName={product.name}
                  image={selectedVariant?.imageUrl || product.image}
                  galleryImages={product.galleryImages}
                />
              </SectionReveal>
            </div>

            {/* 右側 - 資訊 */}
            <div className="flex min-w-0 flex-col lg:w-1/2">
              <SectionReveal delay={1}>
                {/* 系列名稱 */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-semibold text-[#D4A574] uppercase tracking-wider">
                    {product.subtitle}
                  </span>
                  {isNew && (
                    <span className="px-2.5 py-0.5 rounded-full bg-[#D4A574] text-white text-xs font-bold">
                      新品
                    </span>
                  )}
                  {isHot && !isNew && (
                    <span className="px-2.5 py-0.5 rounded-full bg-[#1B3D2A] text-white text-xs font-bold">
                      熱銷
                    </span>
                  )}
                </div>

                <h2 className="break-words text-2xl font-bold text-[#1B3D2A] md:text-3xl">
                  {product.name}
                </h2>

                {/* 標籤 */}
                <div className="mt-3 flex flex-wrap gap-2">
                  {product.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 rounded-full bg-[#1B3D2A]/8 text-sm text-[#1B3D2A] font-medium border border-[#1B3D2A]/10"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* 價格（銷售型商品的價格顯示在下方購物車區塊中） */}
                {product.productType !== 'Sale' && (
                  <div className="mt-6 flex flex-wrap items-baseline gap-2">
                    <span className="text-sm text-[#9A8A7A]">NT$</span>
                    <span className="text-3xl md:text-4xl font-bold text-[#1B3D2A]">
                      {displayPrice.toLocaleString()}
                    </span>
                    {product.salePrice && (
                      <span className="text-base font-semibold text-[#9A8A7A] line-through">
                        NT$ {product.price.toLocaleString()}
                      </span>
                    )}
                  </div>
                )}

                {product.shortDescription && (
                  <p className="mt-4 text-base leading-relaxed text-[#5A6B5A]">
                    {product.shortDescription}
                  </p>
                )}

                {/* 明細重點資訊卡 */}
                {quickInfoItems.length > 0 && (
                  <div className="mt-6 rounded-xl border border-[#E8E0D6] bg-[#FAF8F5] p-5 md:p-6">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {quickInfoItems.map((item) => (
                        <div className="flex items-center gap-3" key={`${item.Label}-${item.Value}`}>
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#1B3D2A]/10">
                            <div className="flex h-4 w-4 items-center justify-center">
                              <i className={`${item.Icon || 'ri-information-line'} text-sm text-[#1B3D2A]`} />
                            </div>
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs text-[#9A8A7A]">{item.Label}</div>
                            <div className="break-words text-sm font-semibold text-[#1B3D2A]">{item.Value}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 購物車區塊（僅銷售型商品顯示） */}
                {product.productType === 'Sale' && (
                  <ProductCartSection
                    product={product}
                    activeVariants={activeVariants}
                    selectedVariant={selectedVariant}
                    selectedOptionValues={selectedOptionValues}
                    onSelectOption={handleSelectVariantOption}
                  />
                )}

                {/* CTA 按鈕 */}
                <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4">
                  <button
                    type="button"
                    onClick={() => navigate(productTrialBookingPath)}
                    className="bg-[#1B3D2A] text-white px-6 md:px-8 py-3.5 rounded-xl text-base font-bold hover:bg-[#143026] transition-colors whitespace-nowrap flex items-center justify-center gap-2 min-h-[52px]"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-calendar-check-line" />
                    </div>
                    預約試戴
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/contact')}
                    className="border border-[#E8E0D6] text-[#5A6B5A] px-6 md:px-8 py-3.5 rounded-xl text-base font-semibold hover:border-[#1B3D2A] hover:text-[#1B3D2A] transition-colors whitespace-nowrap flex items-center justify-center gap-2 min-h-[52px]"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-customer-service-2-line" />
                    </div>
                    聯絡客服諮詢
                  </button>
                </div>
              </SectionReveal>
            </div>
          </div>
        </div>

        {/* 功能特色區 */}
        <div className="px-6 md:px-10 lg:px-16 pb-8 md:pb-12 max-w-7xl mx-auto">
          <ProductFeatures tags={product.tags} features={product.featureItems} />
        </div>

        {/* 規格區 */}
        <div className="px-6 md:px-10 lg:px-16 pb-8 md:pb-12 max-w-7xl mx-auto">
          <ProductSpecs specs={product.specItems} />
        </div>

        {/* 詳細介紹 */}
        <div className="px-6 md:px-10 lg:px-16 pb-8 md:pb-12 max-w-7xl mx-auto">
          <ProductDescriptionSection description={product.description} />
        </div>

        {/* 相關產品 */}
        <div className="px-6 md:px-10 lg:px-16 pb-8 md:pb-12 max-w-7xl mx-auto">
          <RelatedProducts currentProductId={product.id} category={product.category} currentPrice={displayPrice} products={relatedSource} />
        </div>

        {/* 底部 CTA */}
        <div className="px-6 md:px-10 lg:px-16 pb-12 md:pb-20 max-w-7xl mx-auto">
          <SectionReveal delay={2}>
            <div className="bg-[#FAF8F5] rounded-2xl border border-[#E8E0D6] p-6 md:p-10 flex flex-col md:flex-row items-stretch md:items-center gap-5 md:gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-customer-service-2-line text-[#1B3D2A]" />
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-[#D4A574] tracking-wider uppercase">專業建議</span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-[#1B3D2A]">
                  不確定這款是否適合您？
                </h3>
                <p className="mt-3 text-sm text-[#5A6B5A] leading-relaxed">
                  每個人的聽力狀況不同，建議先進行免費聽力檢測，由專業聽力師為您推薦最適合的機型。
                </p>
              </div>
                <button
                  type="button"
                  onClick={() => navigate(productTrialBookingPath)}
                className="shrink-0 bg-[#1B3D2A] text-white px-6 md:px-8 py-3 rounded-xl text-sm font-bold hover:bg-[#143026] transition-colors whitespace-nowrap flex items-center gap-2 min-h-[52px]"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-calendar-check-line" />
                </div>
                預約試戴
              </button>
            </div>
          </SectionReveal>
        </div>
      </main>

      <Footer />
    </div>
  );
}
